import { normalizeBookAbbr } from '../bible/readingLines';
import { BOOK_NAMES } from '../utils/bibleBooks';

// ── Verse timings for the narrated chapter recordings ───────────────────────
// public.audio_timings (src/sql/audio_timings.sql) holds, per recording, when
// each verse starts. scripts/audio-align fills it a few books at a time, so a
// missing row is normal and every caller must carry on as if timings did not
// exist. Everything here except loadTimings is pure, so it can be tested
// without a browser or a database.

// Below this, the aligner heard too little of a verse to trust where it says
// the verse starts; we lean on the nearest verse it did hear instead.
export const MIN_CONFIDENCE = 0.5;

// The language a recording must be in for a row to apply. Spanish readers on
// the Reading page hear the English recording, but the row for that recording
// says 'en', so asking for 'es' keeps Spanish exactly as it was until Spanish
// recordings get timings of their own.
export function timingLangFor(lang) {
  if (lang === 'zh' || lang === 'sc') return 'zh';
  if (lang === 'en' || lang === 'es') return lang;
  return null;
}

// The same recording is stored as https://… in `verses` and http://… in
// bible_chapters and audio_timings, sometimes with the host in other cases.
// What identifies it is the host (without "www.") plus the path, e.g.
// "churchinpiscataway.org/File/Get/<id>"; the id itself is case-sensitive.
export function normaliseAudioKey(url) {
  if (!url || typeof url !== 'string') return null;
  const m = url.trim().match(/^(?:[a-z][a-z0-9+.-]*:)?\/\/([^/?#]+)([^#]*)/i);
  if (!m) return null;
  const host = m[1].toLowerCase().replace(/^www\./, '');
  return host + (m[2] || '/');
}

// A row as the rest of the app uses it: numbers, and an explicit verse list
// (an empty one in the table means the verses run 1, 2, 3 … in order).
export function cleanRow(raw) {
  if (!raw) return null;
  const starts = (raw.starts || []).map(Number);
  const confidence = (raw.confidence || []).map(Number);
  const verses = raw.verses?.length === starts.length
    ? raw.verses.map(Number)
    : starts.map((_, i) => i + 1);
  return {
    book: raw.book, chapter: Number(raw.chapter), lang: raw.lang, audio_url: raw.audio_url,
    duration: Number(raw.duration), verses, starts, confidence,
  };
}

export function rowIs(row, book, chapter) {
  return !!row && row.book === book && row.chapter === Number(chapter);
}

export function verseIndex(row, verse) {
  return row ? row.verses.indexOf(Number(verse)) : -1;
}

export function hasVerse(row, verse) {
  return verseIndex(row, verse) >= 0;
}

const trusted = (row, i) => !(row.confidence[i] < MIN_CONFIDENCE);

// The second to start playing from so `verse` is heard from its first word,
// or null when the row does not know the verse. The chapter's first verse
// starts at 0, keeping the narrator's chapter title and any Psalm heading
// (which the aligner may file as verse 0) in front of it. A start the aligner
// was unsure of falls back to the nearest earlier verse it was sure of: the
// reader hears a little more, never a verse cut in half.
export function verseStart(row, verse) {
  let i = verseIndex(row, verse);
  if (i < 0) return null;
  if (Number(verse) <= 1) return 0;
  while (i > 0 && !trusted(row, i)) i--;
  return i === 0 ? 0 : row.starts[i];
}

// The second `verse` has finished: where the next verse begins, or the end
// of the recording after the last one. As with starts, an unsure next start
// is skipped in favour of a later sure one, so the verse is never cut short.
export function verseEnd(row, verse) {
  const i = verseIndex(row, verse);
  if (i < 0) return null;
  for (let j = i + 1; j < row.starts.length; j++) {
    if (trusted(row, j)) return row.starts[j];
  }
  return row.duration;
}

// The verse being read at `seconds`, or null during the chapter title.
export function verseAt(row, seconds) {
  if (!row || !row.starts.length || seconds < row.starts[0]) return null;
  let lo = 0, hi = row.starts.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (row.starts[mid] <= seconds) lo = mid; else hi = mid - 1;
  }
  return row.verses[lo];
}

// "Mark", "Luke", "John" … appear in titles spelled out, which the reading-line
// abbreviations do not cover.
const BY_ENGLISH_NAME = Object.fromEntries(
  Object.entries(BOOK_NAMES).map(([abbr, n]) => [n.en.toLowerCase().replace(/\s+/g, ''), abbr]));

// Title spellings neither of those knows ("2 Chron. 20:1~…").
const TITLE_ALIASES = { '1chron': '1Chr', '2chron': '2Chr' };

function bookFromName(raw) {
  const name = (raw || '').trim();
  const squashed = name.toLowerCase().replace(/[\s.]+/g, '');
  return normalizeBookAbbr(name) || BY_ENGLISH_NAME[squashed] || TITLE_ALIASES[squashed] || null;
}

// A portion's first and last verse from its title, as the `verses` table and
// the schedule write it:
//   "New Testament - Col. 3:12~4:6"          one book, several chapters
//   "New Testament - Mark 4:1~20"            end in the same chapter
//   "Old Testament - 1 King 22:51~2 King 2:18"  across two books
//   "Old Testament -Job 32:2~34:37", "Num. 7:1-89"
// Returns { first: {book, chapter, verse}, last: {…} } or null when the title
// reads some other way (then nothing is trimmed).
export function portionBounds(title) {
  const clean = (title || '').replace(/^\s*(New|Old) Testament\s*-\s*/i, '').trim();
  const m = clean.match(/^(.+?)\s*(\d+)\s*:\s*(\d+)\s*[~-]\s*(.+)$/);
  // The end: "4:6", "20", or "2 King 2:18" with a book of its own.
  const e = m && m[4].trim().match(/^(?:(.*\D)\s+)?(?:(\d+)\s*:\s*)?(\d+)$/);
  if (!e) return null;
  const firstBook = bookFromName(m[1]);
  const lastBook = e[1] ? bookFromName(e[1]) : firstBook;
  if (!firstBook || !lastBook) return null;
  const first = { book: firstBook, chapter: parseInt(m[2], 10), verse: parseInt(m[3], 10) };
  const last = {
    book: lastBook,
    // "Mark 4:1~20" ends in chapter 4; "1 King 22:51~2 King 2:18" names chapter 2.
    chapter: e[2] ? parseInt(e[2], 10) : first.chapter,
    verse: parseInt(e[3], 10),
  };
  return { first, last };
}

// ── Loading ─────────────────────────────────────────────────────────────────
// One promise per recording key, kept for the session: the rows only change
// when the owner uploads more, and a reload picks those up. A failed request
// is forgotten so the next caller tries again.
const cache = new Map();
const CHUNK = 20;

// PostgREST filter values with commas, dots or brackets must be quoted.
const quote = s => `"${s.replace(/["\\]/g, c => '\\' + c)}"`;

async function queryRows(keys) {
  // Loaded on first use, so the pure helpers above can be tested without a
  // Supabase client being created.
  const { supabase } = await import('../supabase');
  const rows = [];
  for (let i = 0; i < keys.length; i += CHUNK) {
    // The stored URL differs from ours in scheme and host case only, so match
    // on "ends with host + path" ignoring case, then check exactly below.
    const filter = keys.slice(i, i + CHUNK).map(k => `audio_url.ilike.${quote('*' + k)}`).join(',');
    const { data, error } = await supabase.from('audio_timings')
      .select('book,chapter,lang,audio_url,duration,verses,starts,confidence')
      .or(filter);
    if (error) throw error;
    rows.push(...(data || []));
  }
  return rows;
}

// Every timing row for these recording URLs, in one request: resolves to
// { [normaliseAudioKey(url)]: row[] } (an empty list when there is none).
export function loadTimings(urls) {
  const keys = [...new Set((urls || []).map(normaliseAudioKey).filter(Boolean))];
  const missing = keys.filter(k => !cache.has(k));
  if (missing.length) {
    const shared = queryRows(missing);
    for (const k of missing) {
      cache.set(k, shared.then(
        rows => rows.filter(r => normaliseAudioKey(r.audio_url) === k).map(cleanRow),
        () => { cache.delete(k); return []; },
      ));
    }
  }
  return Promise.all(keys.map(k => cache.get(k).then(rows => [k, rows])))
    .then(Object.fromEntries);
}

// The row for one recording in the language it must be in, or null.
export function pickRow(rowsByKey, url, lang) {
  const rows = rowsByKey?.[normaliseAudioKey(url)] || [];
  return (lang && rows.find(r => r.lang === lang && r.starts.length)) || null;
}

// For tests: start each one with nothing cached.
export function clearTimingsCache() { cache.clear(); }
